import type { ExerciseTemplate, Trap } from '@yuan-math/exercises';
import { fraction, roundTo, toLatex, toText } from '@yuan-math/math';
import {
  choiceExercise,
  decimalTolerance,
  fractionExercise,
  fractionTrap,
  numericExercise,
  options,
  template,
  trap,
  within,
} from './authoring.ts';

/**
 * Random variables: distributions, expectation, variance, standard deviation
 * and the continuous case.
 *
 * Three items here are built backwards from a wrong answer that would otherwise
 * be *right*, and each pins a parameter that a comment alone would not keep
 * pinned.
 *
 * `expected-value-weighted` forces the probabilities apart. On a uniform
 * distribution the unweighted mean of the outcomes **is** the expected value,
 * so the item would grade a student who ignored the weights as correct and
 * diagnose nothing.
 *
 * `variance-from-table` keeps its values non-negative, because E(X²) − μ² and
 * E(X²) coincide whenever μ = 0 — and μ = 0 is exactly what a distribution
 * symmetric about zero produces. There the student who never subtracts the
 * square of the mean is right, and `variance-mean-square-not-subtracted` names
 * nobody.
 *
 * `combine-independent-variables` draws its two standard deviations from
 * Pythagorean triples, so √(σ₁² + σ₂²) is a whole number and the trap at
 * σ₁ + σ₂ is unmistakably a different one. `standard-deviations-added` is the
 * most valuable diagnosis in the unit, and it is worth an exact answer to keep
 * it from ever being a rounding away from the right one.
 */

/** Drops any trap repeating the answer or an earlier trap. */
const distinct = (answer: number | string, traps: Trap[]): Trap[] => {
  const seen = new Set<string>([String(answer)]);
  return traps.filter((entry) => {
    if (seen.has(entry.response)) return false;
    seen.add(entry.response);
    return true;
  });
};

/** The stories a small discrete variable is dressed in. */
const COUNT_STORIES = [
  { noun: 'faults', item: 'a component', verb: 'contains' },
  { noun: 'goals', item: 'a match', verb: 'produces' },
  { noun: 'late deliveries', item: 'a week', verb: 'brings' },
  { noun: 'calls', item: 'a five-minute period', verb: 'brings' },
] as const;

export const RANDOM_VARIABLE_TEMPLATES: ExerciseTemplate[] = [
  /* --------------------------------------------- discrete random variables */
  template({
    id: 'distribution-completes-to-one',
    conceptId: 'discrete-random-variables',
    description: 'Find the missing probability in a distribution.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a = rng.int(5, 30) / 100;
      const b = rng.int(5, 30) / 100;
      const c = rng.int(5, 30) / 100;
      const missing = roundTo(1 - a - b - c, 2);
      const story = rng.pick(COUNT_STORIES);

      return numericExercise({
        prompt: `X, the number of ${story.noun} ${story.item} ${story.verb}, takes the values 0, 1, 2 and 3 with probabilities ${a}, ${b}, ${c} and k. What is k?`,
        value: missing,
        tolerance: within(0.005, missing),
        summary: `X takes some value every time, so the four probabilities must total exactly 1: k = 1 − (${a} + ${b} + ${c}) = ${missing}.`,
        hint: 'What must the whole distribution add up to?',
        steps: [
          { label: 'Add the three known probabilities.', latex: `${a} + ${b} + ${c} = ${roundTo(a + b + c, 2)}` },
          { label: 'The total must be 1.', latex: `k = 1 - ${roundTo(a + b + c, 2)} = ${missing}` },
        ],
        traps: distinct(missing, [
          // The three probabilities added, where what is left over was wanted.
          trap(roundTo(a + b + c, 2), 'distribution-does-not-total-one'),
          // Divided among the outcomes rather than taken from 1.
          trap(roundTo((a + b + c) / 3, 2), 'distribution-does-not-total-one'),
        ]),
      });
    },
  }),

  template({
    id: 'cumulative-probability',
    conceptId: 'discrete-random-variables',
    description: 'Accumulate a distribution, with the boundary in play.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const weights = [rng.int(1, 5), rng.int(1, 5), rng.int(1, 5), rng.int(1, 5), rng.int(1, 5)];
      const total = weights.reduce((sum, weight) => sum + weight, 0);
      const cut = rng.int(1, 3);
      const atMost = weights.slice(0, cut + 1).reduce((sum, weight) => sum + weight, 0);
      const below = atMost - (weights[cut] as number);
      const story = rng.pick(COUNT_STORIES);

      return fractionExercise({
        prompt: `X, the number of ${story.noun} ${story.item} ${story.verb}, takes the values 0 to 4 with probabilities ${weights.map((w) => `${w}/${total}`).join(', ')} in order. Find P(X ≤ ${cut}), as a fraction in its simplest form.`,
        value: fraction(atMost, total),
        requireSimplified: true,
        summary: `"At most ${cut}" includes ${cut} itself, so add the probabilities for 0 up to and including ${cut}: ${weights.slice(0, cut + 1).map((w) => `${w}`).join(' + ')} = ${atMost}, over ${total}.`,
        hint: 'Which values of X satisfy the condition? Check whether the boundary value is one of them.',
        steps: [
          { label: `List the values with X ≤ ${cut}.`, latex: `0, \\ldots, ${cut}` },
          { label: 'Add their probabilities.', latex: `\\frac{${atMost}}{${total}}` },
          { label: 'Simplify.', latex: toLatex(fraction(atMost, total)) },
        ],
        traps: distinct(toText(fraction(atMost, total)), [
          // The boundary value dropped: P(X < cut) rather than P(X ≤ cut).
          fractionTrap(fraction(below, total), 'cumulative-boundary-excluded'),
          // The complement, P(X > cut).
          fractionTrap(fraction(total - atMost, total), 'complement-not-subtracted'),
        ]),
      });
    },
  }),

  template({
    id: 'valid-distribution-choice',
    conceptId: 'discrete-random-variables',
    description: 'Identify the list of probabilities that is a valid distribution.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a = rng.int(1, 4);
      const b = rng.int(1, 4);
      const c = 10 - a - b;
      /*
       * Printed to a fixed decimal place. `10 / 10` renders as a bare "1"
       * beside three decimals, which reads as a different kind of quantity —
       * and the four lists are meant to differ only in their arithmetic.
       */
      const tenths = (n: number): string => (n / 10).toFixed(1);

      return choiceExercise({
        prompt: 'A random variable takes the values 1, 2 and 3. Which list of probabilities is a valid distribution?',
        ...options(
          [
            { label: `${tenths(a)}, ${tenths(b)}, ${tenths(c)}`, correct: true },
            { label: `${tenths(a)}, ${tenths(b)}, ${tenths(c + 2)}`, misconceptionId: 'distribution-does-not-total-one' },
            { label: `${tenths(a + 1)}, ${tenths(b + 1)}, ${tenths(c)}`, misconceptionId: 'distribution-does-not-total-one' },
            { label: `${tenths(a)}, ${tenths(b + c)}, -0.1`, misconceptionId: 'distribution-does-not-total-one' },
          ],
          rng.shuffle,
        ),
        summary: `Two conditions: every probability lies between 0 and 1, and they total exactly 1. Only ${tenths(a)} + ${tenths(b)} + ${tenths(c)} = 1 does both.`,
        hint: 'Add each list up — and check none of them contains a negative.',
      });
    },
  }),

  /* ------------------------------------------------------- expected value */
  template({
    id: 'expected-value-weighted',
    conceptId: 'expected-value',
    description: 'A probability-weighted mean, on outcomes that are not equally likely.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      /*
       * The weights are forced apart. On a uniform distribution the unweighted
       * mean *is* the expected value, and the trap below would be the answer —
       * the item would grade a student who ignored the probabilities correct.
       */
      const weights = rng.shuffle([1, 2, 7]);
      const base = [rng.int(1, 4), rng.int(5, 9), rng.int(10, 16)];
      const weightedMean = (vs: number[]): number =>
        vs.reduce((sum, value, i) => sum + value * (weights[i] as number), 0) / 10;
      const plainMean = (vs: number[]): number => vs.reduce((sum, value) => sum + value, 0) / 3;

      /*
       * Forcing the weights apart is not quite enough. The weighted and the
       * plain mean can still land within a hundredth of each other by
       * coincidence, and then the trap is stripped for grading as correct — so
       * the top prize is nudged upwards until the two separate. Deterministic,
       * because the nudge is a function of the drawn values and not of a second
       * draw.
       */
      const values = ((): number[] => {
        for (let bump = 0; bump < 8; bump += 1) {
          const candidate = [base[0] as number, base[1] as number, (base[2] as number) + bump];
          if (Math.abs(weightedMean(candidate) - plainMean(candidate)) >= 0.05) return candidate;
        }
        return base;
      })();
      const expected = roundTo(weightedMean(values), 2);
      const unweighted = roundTo(plainMean(values), 2);

      return numericExercise({
        prompt: `A prize is worth ${values[0]}, ${values[1]} or ${values[2]} pounds, with probabilities ${(weights[0] as number) / 10}, ${(weights[1] as number) / 10} and ${(weights[2] as number) / 10}. What is the expected value of the prize? Give your answer to two decimal places.`,
        unit: '£',
        value: expected,
        tolerance: decimalTolerance(expected, 2),
        summary: `Weight each value by its probability and add: ${values.map((value, i) => `${value} × ${(weights[i] as number) / 10}`).join(' + ')} = ${expected}.`,
        hint: 'The three prizes are not equally likely. What should each one be multiplied by before adding?',
        steps: [
          {
            label: 'Multiply each value by its probability.',
            latex: values.map((value, i) => `${value} \\cdot ${(weights[i] as number) / 10}`).join(' + '),
          },
          { label: 'Add.', latex: `${expected}` },
        ],
        traps: distinct(expected, [
          // The plain average of the outcomes, ignoring how likely each is.
          trap(unweighted, 'expected-value-unweighted'),
        ]),
      });
    },
  }),

  template({
    id: 'expected-value-linear',
    conceptId: 'expected-value',
    description: 'Transform an expectation with E(aX + b) = aE(X) + b.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const mean = rng.int(2, 12);
      const a = rng.int(2, 9);
      const b = rng.int(3, 25);
      const answer = a * mean + b;

      return numericExercise({
        prompt: `X has E(X) = ${mean}. A repair costs ${b} pounds to call out plus ${a} pounds for each unit of X. What is the expected cost?`,
        unit: '£',
        value: answer,
        summary: `The cost is ${a}X + ${b}, and expectation passes straight through a linear transformation: E(${a}X + ${b}) = ${a}(${mean}) + ${b} = ${answer}.`,
        hint: 'The call-out fee is charged on every visit, whatever X turns out to be. Does the multiplier apply to it?',
        steps: [
          { label: 'Write the cost as a transformation of X.', latex: `C = ${a}X + ${b}` },
          { label: 'Apply E(aX + b) = aE(X) + b.', latex: `${a}(${mean}) + ${b} = ${answer}` },
        ],
        traps: distinct(answer, [
          // The constant scaled as well as the variable.
          trap(a * (mean + b), 'expectation-shift-scaled'),
          // The constant forgotten entirely.
          trap(a * mean, 'expectation-shift-scaled'),
        ]),
      });
    },
  }),

  template({
    id: 'expected-profit-fair-game',
    conceptId: 'expected-value',
    description: 'Expected profit, and whether a game is fair.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const stake = rng.int(2, 6);
      const prize = rng.int(8, 30);
      const chances = rng.pick([4, 5, 8, 10, 20]);
      const expectedWin = roundTo(prize / chances, 2);
      const answer = roundTo(expectedWin - stake, 2);

      return numericExercise({
        prompt: `A game costs ${stake} pounds to play and pays ${prize} pounds with probability 1/${chances}, and nothing otherwise. What is the player's expected profit per game? Give your answer to two decimal places, using a minus sign for a loss.`,
        unit: '£',
        value: answer,
        tolerance: decimalTolerance(answer, 2),
        summary: `Expected winnings are ${prize} × 1/${chances} = ${expectedWin}, and the stake of ${stake} is paid every time. Expected profit = ${expectedWin} − ${stake} = ${answer}.`,
        hint: 'The prize arrives sometimes; the stake is paid always. Which of the two needs weighting?',
        steps: [
          { label: 'Expected winnings.', latex: `${prize} \\times \\frac{1}{${chances}} = ${expectedWin}` },
          { label: 'Subtract the stake, which is certain.', latex: `${expectedWin} - ${stake} = ${answer}` },
        ],
        traps: distinct(answer, [
          // The winnings quoted, with the stake never subtracted.
          trap(expectedWin, 'expectation-shift-scaled'),
          // The prize treated as though it were certain.
          trap(roundTo(prize - stake, 2), 'expected-value-unweighted'),
        ]),
      });
    },
  }),

  /* ------------------------------------------------------------- variance */
  template({
    id: 'variance-from-table',
    conceptId: 'random-variable-variance',
    description: 'Compute Var(X) = E(X²) − μ² from a small distribution.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const weights = rng.shuffle([1, 3, 6]);
      /*
       * The values are kept positive and distinct. A distribution symmetric
       * about zero has μ = 0, and then E(X²) − μ² is E(X²) — so the trap
       * naming `variance-mean-square-not-subtracted` would be the answer.
       */
      const values = [rng.int(0, 2), rng.int(3, 6), rng.int(7, 12)];
      const mean = values.reduce((sum, value, i) => sum + value * (weights[i] as number), 0) / 10;
      const meanSquare = values.reduce((sum, value, i) => sum + value * value * (weights[i] as number), 0) / 10;
      const answer = roundTo(meanSquare - mean * mean, 2);

      return numericExercise({
        prompt: `X takes the values ${values[0]}, ${values[1]} and ${values[2]} with probabilities ${(weights[0] as number) / 10}, ${(weights[1] as number) / 10} and ${(weights[2] as number) / 10}. Find Var(X), to two decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 2),
        summary: `μ = ${roundTo(mean, 2)} and E(X²) = ${roundTo(meanSquare, 2)}, so Var(X) = ${roundTo(meanSquare, 2)} − ${roundTo(mean, 2)}² = ${answer}.`,
        hint: 'Two weighted sums are needed: one of the values, one of their squares. The formula subtracts the square of the first from the second.',
        steps: [
          { label: 'The mean.', latex: `\\mu = ${roundTo(mean, 3)}` },
          { label: 'The mean of the squares — square the values, keep the probabilities.', latex: `E(X^{2}) = ${roundTo(meanSquare, 3)}` },
          { label: 'Subtract the square of the mean.', latex: `${roundTo(meanSquare, 3)} - ${roundTo(mean * mean, 3)} = ${answer}` },
        ],
        traps: distinct(answer, [
          // E(X²) reported, with μ² never taken off.
          trap(roundTo(meanSquare, 2), 'variance-mean-square-not-subtracted'),
          // μ² subtracted from μ — averaging and squaring in the wrong order.
          trap(roundTo(mean - mean * mean, 2), 'expectation-of-square-confused'),
          // The standard deviation, where the variance was asked for.
          trap(roundTo(Math.sqrt(Math.max(0, meanSquare - mean * mean)), 2), 'variance-not-rooted'),
        ]),
      });
    },
  }),

  template({
    id: 'variance-linear-transform',
    conceptId: 'random-variable-variance',
    description: 'Apply Var(aX + b) = a²Var(X).',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const variance = rng.int(2, 20);
      const a = rng.int(2, 9);
      const b = rng.int(3, 30);
      const answer = a * a * variance;

      return numericExercise({
        prompt: `X has Var(X) = ${variance}. Find the variance of ${a}X + ${b}.`,
        value: answer,
        summary: `Adding ${b} slides the whole distribution and changes no distance inside it; multiplying by ${a} multiplies every distance by ${a}, so the squared distances grow by ${a}². Var(${a}X + ${b}) = ${a}² × ${variance} = ${answer}.`,
        hint: 'What does adding a constant do to how spread out the values are? And variance is measured in which units?',
        steps: [
          { label: 'The added constant does nothing to spread.', latex: `\\operatorname{Var}(${a}X + ${b}) = \\operatorname{Var}(${a}X)` },
          { label: 'A scale factor arrives squared.', latex: `${a}^{2} \\times ${variance} = ${answer}` },
        ],
        traps: distinct(answer, [
          // The scale factor not squared.
          trap(a * variance, 'variance-scale-not-squared'),
          // The shift added to the variance.
          trap(a * a * variance + b, 'variance-shift-added'),
          // Both errors at once.
          trap(a * variance + b, 'variance-shift-added'),
        ]),
      });
    },
  }),

  template({
    id: 'mean-of-squares-choice',
    conceptId: 'random-variable-variance',
    description: 'Distinguish E(X²) from [E(X)]².',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const low = rng.int(1, 4);
      const high = low + rng.int(3, 8);
      const mean = (low + high) / 2;
      const meanSquare = (low * low + high * high) / 2;

      return choiceExercise({
        prompt: `X takes the value ${low} or ${high}, each with probability 0.5. Which expression gives Var(X)?`,
        ...options(
          [
            { latex: `\\frac{${low}^{2} + ${high}^{2}}{2} - \\left(\\frac{${low} + ${high}}{2}\\right)^{2}`, correct: true },
            {
              latex: `\\frac{${low}^{2} + ${high}^{2}}{2}`,
              misconceptionId: 'variance-mean-square-not-subtracted',
            },
            {
              latex: `\\left(\\frac{${low} + ${high}}{2}\\right)^{2} - \\frac{${low}^{2} + ${high}^{2}}{2}`,
              misconceptionId: 'expectation-of-square-confused',
            },
            { latex: `\\frac{${low} + ${high}}{2} - \\left(\\frac{${low} + ${high}}{2}\\right)^{2}`, misconceptionId: 'expectation-of-square-confused' },
          ],
          rng.shuffle,
        ),
        summary: `Var(X) = E(X²) − μ². Here E(X²) = ${meanSquare} and μ = ${mean}, so the variance is ${meanSquare} − ${mean * mean} = ${roundTo(meanSquare - mean * mean, 2)} — square-then-average minus average-then-square, in that order.`,
        hint: 'One of these squares the values first and one squares the mean. Which order belongs to which term?',
      });
    },
  }),

  /* --------------------------------------- standard deviation and combining */
  template({
    id: 'sd-from-variance',
    conceptId: 'random-variable-sd',
    description: 'Recover the units by taking the square root.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const sd = rng.int(2, 12);
      const variance = sd * sd;
      const a = rng.int(2, 6);
      const answer = a * sd;

      return numericExercise({
        prompt: `X has Var(X) = ${variance} in squared grams. What is the standard deviation of ${a}X, in grams?`,
        unit: 'g',
        value: answer,
        summary: `σ(X) = √${variance} = ${sd} g. Multiplying by ${a} multiplies every distance by ${a}, so σ(${a}X) = ${a} × ${sd} = ${answer} g.`,
        hint: 'Take the square root first, or square the scale factor first — but do not do both to the same quantity.',
        steps: [
          { label: 'The standard deviation of X.', latex: `\\sigma = \\sqrt{${variance}} = ${sd}` },
          { label: 'A scale factor multiplies a standard deviation once.', latex: `${a} \\times ${sd} = ${answer}` },
        ],
        traps: distinct(answer, [
          // The variance of aX, never square-rooted.
          trap(a * a * variance, 'variance-not-rooted'),
          // The scale factor squared as though this were a variance.
          trap(a * a * sd, 'variance-scale-not-squared'),
          // The root never taken.
          trap(a * variance, 'variance-not-rooted'),
        ]),
      });
    },
  }),

  template({
    id: 'combine-independent-variables',
    conceptId: 'random-variable-sd',
    description: 'Combine two independent variables — variances add, not σ.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      /* Pythagorean pairs, so the answer is exact and the trap is visibly wrong. */
      const pair = rng.pick([
        [3, 4, 5],
        [6, 8, 10],
        [5, 12, 13],
        [9, 12, 15],
        [8, 15, 17],
        [7, 24, 25],
      ]);
      const [s1, s2, combined] = pair as [number, number, number];
      const m1 = rng.int(20, 90) * 10;
      const m2 = rng.int(3, 12) * 10;
      const difference = rng.bool();
      const mean = difference ? m1 - m2 : m1 + m2;

      return numericExercise({
        prompt: `Independent quantities A and B have standard deviations ${s1} g and ${s2} g. What is the standard deviation of A ${difference ? '−' : '+'} B, in grams?`,
        unit: 'g',
        value: combined,
        summary: `Convert to variances, combine, then take the root: ${s1}² + ${s2}² = ${s1 * s1 + s2 * s2}, and √${s1 * s1 + s2 * s2} = ${combined} g.${difference ? ' The variances still **add** for a difference — subtracting a variable adds a second source of uncertainty rather than removing one.' : ''}`,
        hint: 'Standard deviations cannot be combined directly. What can be?',
        steps: [
          { label: 'Square both to get variances.', latex: `${s1}^{2} = ${s1 * s1}, \\quad ${s2}^{2} = ${s2 * s2}` },
          { label: 'Independent, so the variances add — even for a difference.', latex: `${s1 * s1} + ${s2 * s2} = ${s1 * s1 + s2 * s2}` },
          { label: 'Take the square root.', latex: `\\sqrt{${s1 * s1 + s2 * s2}} = ${combined}` },
        ],
        traps: distinct(combined, [
          // Standard deviations added directly — the Pythagorean rule ignored.
          trap(s1 + s2, 'standard-deviations-added'),
          // Variances subtracted for a difference.
          ...(difference && s1 !== s2
            ? [trap(roundTo(Math.sqrt(Math.abs(s1 * s1 - s2 * s2)), 2), 'variance-of-difference-subtracted')]
            : []),
          // The combined variance, never square-rooted.
          trap(s1 * s1 + s2 * s2, 'variance-not-rooted'),
          // The mean, offered where the spread was asked for.
          trap(mean, 'spread-judged-by-centre'),
        ]),
      });
    },
  }),

  template({
    id: 'doubling-versus-two-observations',
    conceptId: 'random-variable-sd',
    description: 'Tell 2X apart from X₁ + X₂.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const sd = rng.pick([2, 3, 4, 5, 6, 10]);
      const variance = sd * sd;

      return choiceExercise({
        prompt: `X has standard deviation ${sd}. Two independent observations X₁ and X₂ are taken. Which statement is true?`,
        ...options(
          [
            {
              latex: `\\operatorname{Var}(2X) = ${4 * variance},\\; \\operatorname{Var}(X_1 + X_2) = ${2 * variance}`,
              correct: true,
            },
            {
              latex: `\\operatorname{Var}(2X) = \\operatorname{Var}(X_1 + X_2) = ${2 * variance}`,
              misconceptionId: 'repeated-observation-taken-as-scaling',
            },
            {
              latex: `\\operatorname{Var}(2X) = \\operatorname{Var}(X_1 + X_2) = ${4 * variance}`,
              misconceptionId: 'repeated-observation-taken-as-scaling',
            },
            {
              latex: `\\operatorname{Var}(2X) = ${2 * variance},\\; \\operatorname{Var}(X_1 + X_2) = ${4 * variance}`,
              misconceptionId: 'variance-scale-not-squared',
            },
          ],
          rng.shuffle,
        ),
        summary: `Doubling one observation doubles its error with it: Var(2X) = 2²σ² = ${4 * variance}. Two separate observations let their errors partly cancel: Var(X₁ + X₂) = σ² + σ² = ${2 * variance}. Same mean, different spread.`,
        hint: 'One of these is a single measurement scaled up; the other is two measurements added. Do their errors behave the same way?',
      });
    },
  }),

  /* ------------------------------------- continuous random variables */
  template({
    id: 'uniform-density-height',
    conceptId: 'continuous-random-variables',
    description: 'Complete a uniform density using the total-area rule.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const width = rng.pick([2, 4, 5, 8, 10, 16, 20, 25]);
      const answer = roundTo(1 / width, 4);

      return numericExercise({
        prompt: `A waiting time is equally likely to be anywhere between 0 and ${width} minutes, so its density is a rectangle of height h across that range. What is h? Give your answer to three decimal places.`,
        value: roundTo(answer, 3),
        tolerance: decimalTolerance(roundTo(answer, 3), 3),
        figure: {
          kind: 'density',
          points: [
            { x: 0, y: 0 },
            { x: 0, y: answer },
            { x: width, y: answer },
            { x: width, y: 0 },
          ],
          marks: [0, width],
          unitLabel: 'minutes',
        },
        summary: `The total area under a density is exactly 1, and this one is a rectangle: ${width}h = 1, so h = ${roundTo(answer, 3)}.`,
        hint: 'What must the area of the whole rectangle be?',
        steps: [
          { label: 'Area of the rectangle.', latex: `${width}h` },
          { label: 'A density encloses an area of 1.', latex: `${width}h = 1` },
          { label: 'Solve.', latex: `h = ${roundTo(answer, 3)}` },
        ],
        traps: distinct(roundTo(answer, 3), [
          // The width itself, or 1 — a density confused with a probability.
          trap(1, 'density-height-taken-as-probability'),
          trap(width, 'density-height-taken-as-probability'),
        ]),
      });
    },
  }),

  template({
    id: 'uniform-interval-probability',
    conceptId: 'continuous-random-variables',
    description: 'A probability as the area of a rectangle.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const width = rng.pick([4, 5, 8, 10, 20]);
      const from = rng.int(1, width - 3);
      const to = from + rng.int(1, Math.min(4, width - from));
      const height = 1 / width;
      const answer = roundTo((to - from) / width, 3);

      return numericExercise({
        prompt: `A bus is equally likely to arrive at any moment in the next ${width} minutes. What is the probability it arrives between ${from} and ${to} minutes from now? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        figure: {
          kind: 'density',
          points: [
            { x: 0, y: 0 },
            { x: 0, y: roundTo(height, 4) },
            { x: width, y: roundTo(height, 4) },
            { x: width, y: 0 },
          ],
          shade: { from, to },
          marks: [0, from, to, width],
          unitLabel: 'minutes',
        },
        summary: `The density has height 1/${width}, and the strip is ${to} − ${from} = ${to - from} minutes wide. Area = ${to - from} × 1/${width} = ${answer}.`,
        hint: 'Probability is the area of the shaded strip — and the area of a rectangle is height times width.',
        steps: [
          { label: 'Height of the density.', latex: `\\frac{1}{${width}}` },
          { label: 'Width of the strip.', latex: `${to} - ${from} = ${to - from}` },
          { label: 'Multiply.', latex: `\\frac{${to - from}}{${width}} = ${answer}` },
        ],
        traps: distinct(answer, [
          // The height quoted as the probability.
          trap(roundTo(height, 3), 'density-height-taken-as-probability'),
          // The width of the strip, with no division at all.
          trap(to - from, 'density-height-taken-as-probability'),
        ]),
      });
    },
  }),

  template({
    id: 'point-probability-continuous',
    conceptId: 'continuous-random-variables',
    description: 'Why a single value has probability zero.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const width = rng.pick([4, 5, 8, 10, 20]);
      const point = rng.int(1, width - 1);

      return choiceExercise({
        prompt: `T is uniform on [0, ${width}]. What is P(T = ${point}), and how do P(T < ${point}) and P(T ≤ ${point}) compare?`,
        ...options(
          [
            { label: `P(T = ${point}) = 0, and the two inequalities give the same answer`, correct: true },
            {
              label: `P(T = ${point}) = 1/${width}, and P(T ≤ ${point}) is 1/${width} larger`,
              misconceptionId: 'density-height-taken-as-probability',
            },
            {
              label: `P(T = ${point}) = 0, but P(T ≤ ${point}) is still the larger of the two`,
              misconceptionId: 'cumulative-boundary-excluded',
            },
            {
              label: `P(T = ${point}) = ${point}/${width}, and the two inequalities differ`,
              misconceptionId: 'density-height-taken-as-probability',
            },
          ],
          rng.shuffle,
        ),
        summary: `A single point has zero width, so the area above it is zero: P(T = ${point}) = 0. Adding a region of zero area changes nothing, so P(T < ${point}) and P(T ≤ ${point}) are equal — the boundary care that a discrete variable demands is simply unnecessary here.`,
        hint: 'Probability is area. What is the area of a strip with no width?',
      });
    },
  }),
];
